import { LocationHelper, RequestHelper } from '@/helpers/index';
import { Page } from '@/utils/elementIds';
import { useProgressStore } from '@/stores/modules/progressStore'
import { useConfigStore } from '@/stores/modules/configStore'
import * as Sentry from '@sentry/vue';
import { FLOW_ROUTES, type FormType, type PageName } from '@/constants/flows';
import { usePrepopFormState } from '@/composables/usePrepopFormState';
import { nextTick, watch } from 'vue';
import { useFormStore } from '@/stores/modules/formStore'

/**
 * Mapping of page identifiers to their corresponding Vue component imports.
 * Uses lazy loading to improve initial load performance.
 */
export const PAGE_COMPONENTS = {
    'finance-details': () => import('@/views/FinanceDetails.vue'),
    'driving-licence': () => import('@/views/DrivingLicence.vue'),
    'marital-status': () => import('@/views/MaritalStatus.vue'),
    'date-of-birth': () => import('@/views/DateOfBirth.vue'),
    'address-add': () => import('@/views/AddressAdd.vue'),
    'address-status': () => import('@/views/AddressStatus.vue'),
    'address-length': () => import('@/views/AddressLength.vue'),
    'employment': () => import('@/views/EmploymentStatus.vue'),
    'employment-location': () => import('@/views/EmploymentLocation.vue'),
    'employment-length': () => import('@/views/EmploymentLength.vue'),
    'employment-income': () => import('@/views/EmploymentIncome.vue'),
    'amount-to-finance': () => import('@/views/AmountToFinance.vue'),
    'contact-details-part-1': () => import('@/views/ContactDetailsPart1.vue'),
    'contact-details-part-2': () => import('@/views/ContactDetailsPart2.vue'),
    'dealer-get-my-quote': () => import('@/views/DealerGetMyQuote.vue'),
    'dealer-search': () => import('@/views/DealerSearch.vue'),
    'get-my-quote': () => import('@/views/GetMyQuote.vue'),
    'pre-pop-form': () => import('@/views/PrePopForm.vue'),
    'pre-pop-landing': () => import('@/views/PrePopLanding.vue'),
    'submission-type': () => import('@/views/SubmissionType.vue'),
    'refinance-form': () => import('@/views/RefinanceForm.vue')
} as const;

export type StartPage = keyof typeof PAGE_COMPONENTS;

export interface QueryParams {
    [key: string]: string | boolean | undefined;
}

/**
 * Helper function to convert query parameters to the correct type
 */
export function getQueryParams(): QueryParams {
    console.log('🔍 getQueryParams - Starting function');
    const params: QueryParams = {};
    
    // Get URL parameters directly from the search string
    const searchString = window.location.search.substring(1);
    const searchParams = new URLSearchParams(searchString);
    const rawParams = Object.fromEntries(searchParams.entries());
    
    console.log('🔍 Raw URL search string:', searchString);
    console.log('🔍 Raw URL parameters:', rawParams);
    
    // Check for appId first - case insensitive
    const appId = searchString.match(/[?&]appid=([^&]+)/i)?.[1];
    if (appId) {
        console.log('📱 Found appId in URL:', appId);
    }
    
    const host = LocationHelper.getHost();
    
    // Convert all query parameters to strings or undefined
    const processedParams = {
        appId: appId || rawParams['appId'] || rawParams['appid'],
        vehicleType: rawParams['vehicleType'] || rawParams['vehicle_type'],
        vehicleId: rawParams['vehicleId'] || rawParams['vehicle_id'] || rawParams['vehicleid'],
        vrm: rawParams['vrm'],
        advertId: rawParams['advertId'] || rawParams['advert_id'] || rawParams['advertid'],
        personalised: rawParams['personalised'],
        isCarCraft: host.startsWith('carcraft'),
        isCKDirect: host.startsWith('ckdirect'),
        isCompareTheMarket: host.startsWith('comparethemarket') || host.startsWith('comparethemarketcarfinance'),
        isSocial: ['social', 'ecrm', 'tiktok', 'social-premier', 'social-vans', 'social-bad-credit'].includes(host),
        aid: rawParams['aid'] || rawParams['aId'],
        daid: rawParams['daid'] || rawParams['daId'],
        noPx: rawParams['noPx'],
        did: rawParams['did'] || rawParams['dId'],
        submissionTypeId: rawParams['submissionTypeId'] || rawParams['submissionTypeid'],
        toDealerGetMyQuote: rawParams['to-dealer-get-my-quote'],
        prePopulated: rawParams['prePopulated'],
        direct: rawParams['direct']
    };

    console.log('🔍 Processed query parameters:', processedParams);

    // Convert all values to strings or undefined, but preserve boolean values for specific keys
    Object.entries(processedParams).forEach(([key, value]) => {
        if (value === null || value === undefined) {
            params[key] = undefined;
        } else if (typeof value === 'boolean' && !['isCarCraft', 'isCKDirect', 'isCompareTheMarket', 'isSocial'].includes(key)) {
            // Convert booleans to strings, except for the host-based boolean flags
            params[key] = value.toString();
        } else {
            params[key] = value;
        }
    });

    console.log('📋 Final query parameters:', params);
    console.log('🔍 getQueryParams - Returning params:', params);
    return params;
}

/**
 * Efficiently waits for pagesToComplete to be populated in the progress store
 * Uses Vue's reactivity system with a watcher for optimal performance
 * @param progressStore - The progress store instance
 * @returns Promise<string[]> - The pagesToComplete array
 */
async function waitForPagesToComplete(progressStore: any): Promise<string[]> {
    // First check if pagesToComplete is already populated
    if (progressStore.pagesToComplete && progressStore.pagesToComplete.length > 0) {
        console.log('✅ pagesToComplete already populated:', progressStore.pagesToComplete);
        return progressStore.pagesToComplete;
    }
    
    return new Promise((resolve) => {
        const maxWaitTime = 3000; // 3 seconds max wait (reduced from 5)
        const startTime = Date.now();
        
        // Use Vue's watch for reactive updates
        const unwatch = watch(
            () => progressStore.pagesToComplete,
            (newPagesToComplete) => {
                if (newPagesToComplete && newPagesToComplete.length > 0) {
                    console.log('✅ pagesToComplete populated via watcher:', newPagesToComplete);
                    unwatch();
                    resolve(newPagesToComplete);
                }
            },
            { immediate: true }
        );
        
        // Fallback timeout in case the watcher doesn't trigger
        const timeoutId = setTimeout(() => {
            unwatch();
            console.warn('⚠️ Timeout waiting for pagesToComplete, returning current value');
            resolve(progressStore.pagesToComplete || []);
        }, maxWaitTime);
        
        // Clean up timeout if watcher resolves first
        const originalResolve = resolve;
        resolve = (value) => {
            clearTimeout(timeoutId);
            originalResolve(value);
        };
    });
}

/**
 * Array of rules to determine the start page based on query parameters and store state.
 * Each rule is a function that takes query parameters and store references, and returns either a StartPage or null.
 * Rules are evaluated in order, and the first non-null result is used.
 * Total: 12 rules (1-11, with 4.5 for dealer get my quote)
 */
const pageResolutionRules: Array<(params: QueryParams, stores: { progressStore: any, configStore: any, formStore: any }) => Promise<StartPage | null> | StartPage | null> = [
    // Rule 1: Refinance form (prioritize over pre-pop)
    (params, { progressStore }) => {
        if (params.submissionTypeId === '1') {
            progressStore.setFormType('refinance');
            return 'refinance-form' as StartPage;
        }
        return null;
    },
    // Rule 2: Pre-populated form
    // SIMPLIFIED: Router guard now handles formType setting and data fetching for direct navigation
    // This rule only handles start page determination when going through root route
    async (params, { progressStore, configStore, formStore }) => {
        // Check for appId directly in the URL (case-insensitive) or in params
        const appIdMatch = window.location.search.match(/[?&]appid=([^&]+)/i);
        const appId = appIdMatch ? appIdMatch[1] : null;
        const paramAppId = params.appId;
        
        console.log('🔍 Rule 2 Debug - Pre-populated form check:', {
            appId,
            paramAppId,
            hasAppId: !!(appId || paramAppId),
            affiliateConfig: configStore.affiliateConfig,
            prePopulateForm: configStore.affiliateConfig?.prePopulateForm,
            condition: !!(appId || paramAppId) && configStore.affiliateConfig?.prePopulateForm
        });
        
        if ((appId || paramAppId) && configStore.affiliateConfig?.prePopulateForm) {
            console.log('✅ Rule 2 matched - setting formType to prePopulated');
            progressStore.setFormType('prePopulated');
            
            try {
                // Fetch and populate data - pass the appId parameter
                const applicationId = appId || paramAppId;
                await formStore.fetchAndPopulateData(applicationId!);
                
                // Wait for pagesToComplete to be populated using a more efficient approach
                const pagesToComplete = await waitForPagesToComplete(progressStore);
                
                if (pagesToComplete && pagesToComplete.length > 0) {
                    const firstPage = pagesToComplete[0];
                    return firstPage as StartPage;
                }
                
                // If no pagesToComplete, return pre-pop-form directly
                return 'pre-pop-form' as StartPage;
            } catch (error) {
                Sentry.captureException(error);
                // Fallback to pre-pop-form on error
                return 'pre-pop-form' as StartPage;
            }
        }
        console.log('❌ Rule 2 did not match - continuing to next rule');
        return null;
    },
    // Rule 3: Direct form
    (params, { progressStore }) => {
        if (params.direct === 'true') {
            progressStore.setFormType('direct');
            return 'finance-details' as StartPage;
        }
        return null;
    },
    // Rule 4: Dealer flow with daid and vrm
    (params, { progressStore }) => {
        if (params.daid && params.vrm) {
            progressStore.setFormType('dealerGetMyQuote');
            return 'dealer-get-my-quote' as StartPage;
        }
        return null;
    },
    // Rule 4.5: Dealer get my quote (from dealer search)
    (params, { progressStore }) => {
        if (params.toDealerGetMyQuote === 'true') {
            progressStore.setFormType('dealerGetMyQuote');
            return 'dealer-get-my-quote' as StartPage;
        }
        return null;
    },
    // Rule 5: Dealer search
    (params, { progressStore }) => {
        if (params.did) {
            progressStore.setFormType('dealerSearch');
            return 'dealer-search' as StartPage;
        }
        return null;
    },
    // Rule 6: Vehicle type in URL (special handling)
    (params, { progressStore }) => {
        if (window.location.search.includes('vehicle_type=') || window.location.search.includes('vehicleType=')) {
            progressStore.setFormType('partnerWithVehicleType');
            return 'driving-licence' as StartPage;
        }
        return null;
    },
    // Rule 7: Partner with vehicle identifier (block if daid is present)
    (params, { progressStore }) => {
        const { vehicleId, vrm, advertId, daid } = params;
        // Don't run this rule if we have a dealer ID
        if (daid && typeof daid === 'string' && daid.trim() !== '') {
            return null;
        }
        if ((vehicleId || vrm || advertId)) {
            progressStore.setFormType('partnerWithVehicleId');
            return 'get-my-quote' as StartPage;
        }
        return null;
    },
    // Rule 8: Submission type rule
    (params, { progressStore }) => {
        // Don't run this rule if there are vehicle identifiers
        if (hasVehicleIdentifier(params)) {
            return null;
        }
        
        const { isCompareTheMarket, isSocial, isCarCraft } = params;
        const isSpecialCase_1 = isCompareTheMarket || isSocial || 
                             (isCarCraft && !hasVehicleIdentifier(params)) || 
                             (LocationHelper.getHost() !== 'default' && !hasVehicleIdentifier(params));

        if (isSpecialCase_1) {
            progressStore.setFormType('submissionType');
            return 'submission-type' as StartPage;
        }
        return null;
    },
    // Rule 9: Personalised flow (only if no vehicle identifier)
    (params, { progressStore }) => {
        if (params.personalised === 'true' && !hasVehicleIdentifier(params)) {
            progressStore.setFormType('direct');
            return 'contact-details-part-1' as StartPage;
        }
        return null;
    },
    // Rule 10: Host-based form type (match substrings)
    (params, { progressStore }) => {
        const host = LocationHelper.getHost();
        const hostStr = (host || '').toLowerCase();
        const isSpecialHost = hostStr.includes('carcraft') || hostStr.includes('ckdirect') || hostStr.includes('comparethemarket') || hostStr.includes('social');
        if (isSpecialHost) {
            progressStore.setFormType('submissionType');
            return 'finance-details' as StartPage;
        }
        return null;
    },
    // Rule 11: Existing form state
    (params, { progressStore }) => {
        const existingFormType = progressStore.formType;
        console.log('🔍 Rule 11 Debug - Existing form state check:', {
            existingFormType,
            prePopulated: params.prePopulated
        });
        
        if (existingFormType) {
            if (existingFormType === 'prePopulated' && !params.prePopulated) {
                console.log('🔄 Rule 11: Changing from prePopulated to direct due to !params.prePopulated');
                progressStore.setFormType('direct');
                return 'finance-details' as StartPage;
            }
            console.log('🔄 Rule 11: Using existing form type:', existingFormType);
            return 'finance-details' as StartPage;
        }
        console.log('🔄 Rule 11: No existing form type, setting to direct');
        progressStore.setFormType('direct');
        return 'finance-details' as StartPage;
    }
];

/**
 * Resolves the start page based on the current state and query parameters.
 * @param params - Query parameters from the URL
 * @returns Promise<StartPage> - The resolved start page
 */
export async function resolveStartPage(params: QueryParams): Promise<StartPage> {
    try {
        console.log('🚀 Starting resolveStartPage with params:', params);
        
        const configStore = useConfigStore();
        const progressStore = useProgressStore();
        const formStore = useFormStore();
        
        console.log('🔍 Store states at start:', {
            affiliateConfig: configStore.affiliateConfig,
            prePopulateForm: configStore.affiliateConfig?.prePopulateForm,
            formType: progressStore.formType
        });
    
        // Check each rule in order
        for (let i = 0; i < pageResolutionRules.length; i++) {
            const rule = pageResolutionRules[i];
            const ruleNumber = i === 4 ? '4.5' : (i + 1).toString();
            console.log(`🔄 Checking rule ${ruleNumber}...`);
            const result = await rule(params, { progressStore, configStore, formStore });
            if (result) {
                console.log(`✅ Rule ${ruleNumber} resolved to:`, result);
                console.log('📋 Final form type:', progressStore.formType);
                return result;
            }
        }
    
        // This part should now be effectively unreachable, but kept as a safeguard
        console.log('⚠️ No rules matched, defaulting to finance-details');
        progressStore.setFormType('direct');
        return 'finance-details' as StartPage;
    } catch (error) {
        console.error('An error occurred during page resolution, defaulting to finance-details', error);
        Sentry.captureException(error);
        const progressStore = useProgressStore();
        progressStore.setFormType('direct');
        return 'finance-details' as StartPage;
    }
}

/**
 * Checks if any vehicle identifier is present in the parameters.
 */
function hasVehicleIdentifier(params: QueryParams): boolean {
    return Boolean(params.vehicleId || params.vrm || params.advertId);
}

const PAGE_ROUTE_NAMES = {
    'finance-details': 'finance-details',
    'driving-licence': 'driving-licence',
    'marital-status': 'marital-status',
    'date-of-birth': 'date-of-birth',
    'address-add': 'address-add',
    'address-status': 'address-status',
    'address-length': 'address-length',
    'employment': 'employment',
    'employment-location': 'employment-location',
    'employment-length': 'employment-length',
    'employment-income': 'employment-income',
    'amount-to-finance': 'amount-to-finance',
    'contact-details-part-1': 'contact-details-p1',
    'contact-details-part-2': 'contact-details-p2',
    'dealer-get-my-quote': 'dealer-get-my-quote',
    'dealer-search': 'dealer-search',
    'get-my-quote': 'get-my-quote',
    'pre-pop-form': 'pre-pop-form',
    'pre-pop-landing': 'pre-pop-landing',
    'submission-type': 'submission-type',
    'refinance-form': 'refinance-form'
} as const;

/**
 * Helper module for determining and loading the appropriate start page component
 * based on URL parameters and host information.
 */
export default {
    /**
     * Gets the appropriate Vue component for the start page.
     */
    async getStartPageComponent() {
        console.log('🎬 Starting getStartPageComponent');
        const params = getQueryParams();
        console.log('🎬 getStartPageComponent - After getQueryParams call:', params);
        const progressStore = useProgressStore();
        
        console.log('🔍 getStartPageComponent - Initial params:', params);
        console.log('🔍 getStartPageComponent - URL search:', window.location.search);
        
        // Priority handling for dealer flow when daid and vrm are present
        if (params.daid && params.vrm) {
            console.log('🚗 Dealer flow detected with daid and vrm');
            progressStore.setFormType('dealerGetMyQuote');
            const componentLoader = PAGE_COMPONENTS['dealer-get-my-quote'];
            return componentLoader();
        }
        
        // DIRECT CHECK FOR VEHICLE_TYPE - only if dealer flow not applicable
        if (window.location.search.includes('vehicle_type=') || window.location.search.includes('vehicleType=')) {
            console.log('🚗 Vehicle type detected in URL');
            console.log('🔍 Vehicle type check - setting formType to partnerWithVehicleType');
            // Force the right behavior regardless of parameter parsing
            progressStore.setFormType('partnerWithVehicleType');
            
            // Return the driving licence component using the component loader
            const componentLoader = PAGE_COMPONENTS['driving-licence'];
            return componentLoader();
        }
        
        // Use resolveStartPage instead of determineStartPage
        console.log('🔍 Resolving start page...');
        const startPage = await resolveStartPage(params);
        console.log('📄 Resolved start page:', startPage);
        
        // Validate that the determined start page is compatible with the current form type
        if (startPage && progressStore.formType) {
            try {
                console.log('🔍 Validating page compatibility', {
                    startPage,
                    formType: progressStore.formType
                });
                
                const currentFlow = FLOW_ROUTES[progressStore.formType as Exclude<FormType, ''>];
                const pageInFlow = currentFlow?.pages.includes(PAGE_ROUTE_NAMES[startPage] as PageName);
                
                console.log('📋 Flow validation result:', {
                    hasCurrentFlow: !!currentFlow,
                    pageInFlow,
                    flowPages: currentFlow?.pages
                });
                
                if (!pageInFlow) {
                    console.log('⚠️ Page not in current flow, searching for compatible form type');
                    // Find compatible form type for this start page
                    for (const [formTypeKey, flow] of Object.entries(FLOW_ROUTES)) {
                        if (flow.pages.includes(PAGE_ROUTE_NAMES[startPage] as PageName)) {
                            console.log('✅ Found compatible form type:', formTypeKey);
                            progressStore.setFormType(formTypeKey as FormType);
                            break;
                        }
                    }
                }
            } catch (e) {
                console.error('❌ Error validating start page compatibility:', e);
            }
        }
        
        const componentLoader = PAGE_COMPONENTS[startPage];
        console.log('🎯 Loading component for page:', startPage);
        return componentLoader();
    },

    /**
     * Gets the name of the start page route.
     */
    async getStartPageName() {
        const params = getQueryParams();
        // Use resolveStartPage instead of determineStartPage
        const startPage = await resolveStartPage(params);
        const routeName = PAGE_ROUTE_NAMES[startPage];
        return routeName;
    },

    /**
     * Gets the route name for a given page
     * @param page The page to get the route name for
     * @returns The route name
     */
    getRouteName(page: StartPage): string {
        return PAGE_ROUTE_NAMES[page];
    },

    /**
     * Gets the component for a given page
     * @param page The page to get the component for
     * @returns The component
     */
    getComponent(page: StartPage): any {
        return PAGE_COMPONENTS[page];
    },

    /**
     * Gets the start page and component
     * @param params Query parameters from the URL
     * @returns Object containing the start page and component
     */
    async getStartPageAndComponent(params: QueryParams): Promise<{ startPage: StartPage, component: any }> {
        const startPage = await resolveStartPage(params);
        return {
            startPage,
            component: this.getComponent(startPage)
        };
    }
};